import * as THREE from "three";
import {
  BufferGeometry,
  Camera,
  Clock,
  Scene,
  TubeGeometry,
  Vector3,
} from "three";
import FirstCameraControl from "./FirstCameraControl";

type AnimationFunction = (
  clock: Clock,
  scene: Scene,
  camera: Camera,
  channelData: Uint8Array | undefined
) => void;

const CameraFollowControl = (mesh: THREE.Mesh): AnimationFunction => {
  let initialized = false;
  let controls: FirstCameraControl;
  let lastCall: number = performance.now();
  // ANIMATE
  function animate(
    clock: Clock,
    scene: Scene,
    camera: Camera,
    channelData: Uint8Array | undefined
  ) {
    if (!mesh.visible) return;
    if (!initialized) {
      controls = new FirstCameraControl(
        camera,
        document.getElementById("canvas") as HTMLCanvasElement,
        new Vector3(-5, 1, -5),
        new Vector3(5, 1, 5)
      );
      controls.lookSpeed = 5e-2;
      controls.movementSpeed = 1;
      initialized = true;
    }
    controls.update(performance.now() / 1000.0 - lastCall);
    lastCall = performance.now() / 1000.0;
  }
  return animate;
};

const SineWaveBufferGeometry = (mesh: THREE.Mesh): AnimationFunction => {
  const geometry = mesh.geometry as BufferGeometry;
  const count: number = geometry.attributes.position.count;
  const position_clone = JSON.parse(
    JSON.stringify(geometry.attributes.position.array)
  ) as Float32Array;
  const normals_clone = JSON.parse(
    JSON.stringify(geometry.attributes.normal.array)
  ) as Float32Array;
  const damping = 0.1;
  let initialized = false;
  let controls: FirstCameraControl;
  let lastCall: number = performance.now();
  // ANIMATE
  function animate(
    clock: Clock,
    scene: Scene,
    camera: Camera,
    channelData: Uint8Array | undefined
  ) {
    if (!mesh.visible) return;
    if (!initialized) {
      controls = new FirstCameraControl(
        camera,
        document.getElementById("canvas") as HTMLCanvasElement
      );
      controls.lookSpeed = 3e-2;
      controls.movementSpeed = 0;
      camera.lookAt(mesh.position);
      mesh.position.set(0, 0, 0);
      initialized = true;
    }
    camera.position.set(mesh.position.x, mesh.position.y, mesh.position.z + 5);
    controls.update(performance.now() / 1000.0 - lastCall);
    lastCall = performance.now() / 1000.0;

    // mesh.scale.set(2, 2, 2);
    const fac = Math.sin(lastCall) + 1;
    const val = mesh.scale.lerp(new Vector3(fac, fac, fac), 0.01);
    mesh.scale.set(val.x, val.y, val.z);
    mesh.rotateOnAxis(new Vector3(0, 1, 0), Math.asin(fac * 0.01));

    // iterate all vertices
    for (let i = 0; i < count; i++) {
      // indices
      const ix = i * 3;
      const iy = i * 3 + 1;
      const iz = i * 3 + 2;

      // use uvs to calculate wave
      const uX = geometry.attributes.uv.getX(i) * Math.PI * 16;
      const uY = geometry.attributes.uv.getY(i) * Math.PI * 16;

      // calculate current vertex wave height
      const xangle = uX + lastCall;
      const xsin = Math.sin(xangle) * damping;
      const yangle = uY + lastCall;
      const ycos = Math.cos(yangle) * damping;

      // set new position
      geometry.attributes.position.setX(
        i,
        position_clone[ix] + normals_clone[ix] * (xsin + ycos)
      );
      geometry.attributes.position.setY(
        i,
        position_clone[iy] + normals_clone[iy] * (xsin + ycos)
      );
      geometry.attributes.position.setZ(
        i,
        position_clone[iz] + normals_clone[iz] * (xsin + ycos)
      );

      if (typeof channelData != "undefined") {
        const index = i % channelData.length;
        const value = channelData[index];
        geometry.attributes.position.setZ(
          i,
          geometry.attributes.position.getZ(i) + (0.5 * value) / 500
        );
        geometry.attributes.position.setX(
          i,
          geometry.attributes.position.getX(i) + (0.5 * value) / 500
        );
      }
    }
    geometry.computeVertexNormals();
    geometry.attributes.position.needsUpdate = true;
  }
  return animate;
};

const CameraFollowTube = (mesh: THREE.Mesh): AnimationFunction => {
  const loopTime = 72;
  const geometry = mesh.geometry as TubeGeometry;
  let ctrl1Buffer: number[] = [];
  let ctrl1Mu: number = 0.0;
  let ctrl1Std: number = 0.0;

  function animate(
    clock: Clock,
    scene: Scene,
    camera: Camera,
    channelData: Uint8Array | undefined
  ) {
    if (!mesh.visible) return;
    const time = clock.getElapsedTime();
    const t = (time % loopTime) / loopTime;
    const t2 = ((time + 0.1) % loopTime) / loopTime;
    const pos = geometry.parameters.path.getPointAt(t);
    const pos2 = geometry.parameters.path.getPointAt(t2);
    camera.position.set(pos.x, pos.y + 4, pos.z);
    if (typeof channelData != "undefined") {
      let ctrl1 = 0.0;
      for (let i = 0; i < channelData.length; i += 1) {
        ctrl1 += channelData[i];
      }
      ctrl1 /= channelData.length;
      ctrl1Buffer.push(ctrl1);
      if (ctrl1Buffer.length == 100) {
        ctrl1Mu =
          ctrl1Buffer.reduce((prev, next, index, arr) => prev + next) /
          ctrl1Buffer.length;
        ctrl1Std = Math.sqrt(
          ctrl1Buffer
            .map((val, index, arr) => {
              return (val - ctrl1Mu) ** 2;
            })
            .reduce((prev, next, index, arr) => prev + next)
        );
        ctrl1Buffer = [];
      }
      ctrl1 = Math.min(
        1,
        ((ctrl1 - ctrl1Mu) / Math.max(1e-4, ctrl1Std * 1e-2)) * 0.01
      );
      const loc = new Vector3(pos2.x, pos2.y + 4, pos2.z);
      camera.lookAt(
        loc.lerp(new Vector3(pos2.x, pos2.y + (4 + ctrl1), pos2.z), 0.01)
      );
    } else {
      camera.lookAt(new Vector3(pos2.x, pos2.y + 4, pos2.z));
    }
  }
  return animate;
};

export { SineWaveBufferGeometry, CameraFollowTube, CameraFollowControl };

export type { AnimationFunction };
